/* eslint-disable no-console */
import fs from 'fs';
import pixelmatch from 'pixelmatch';
import { PNG } from 'pngjs/browser.js';

/**
 * https://github.com/mapbox/pixelmatch/issues/25
 * Cretes a copy of {@link img}, with the {@link dimensions}.
 * @param {PNG} img
 * @param {{width: number, height: number}} dimensions
 * @returns {PNG}
 */
function createResized(img, dimensions) {
  if (img.width > dimensions.width || img.height > dimensions.height) {
    throw new Error(
      `New dimensions expected to be greater than or equal to the original dimensions!`,
    );
  }
  const resized = new PNG(dimensions);
  PNG.bitblt(img, resized, 0, 0, img.width, img.height);

  return resized;
}

/**
 * https://github.com/mapbox/pixelmatch/issues/25
 * Creates a diff of the given input images, which may have different sizes.
 * @param {PNG} img1
 * @param {PNG} img2
 * @param {pixelmatch.PixelmatchOptions} options
 * @returns {{diff: PNG, numOfDiffPixels: number}}
 */
function createDiff(img1, img2, options) {
  const diffDimensions = {
    width: Math.max(img1.width, img2.width),
    height: Math.max(img1.height, img2.height),
  };

  const resizedImg1 = createResized(img1, diffDimensions);
  const resizedImg2 = createResized(img2, diffDimensions);

  const diff = new PNG(diffDimensions);

  const numOfDiffPixels = pixelmatch(
    resizedImg1.data,
    resizedImg2.data,
    diff.data,
    diffDimensions.width,
    diffDimensions.height,
    options,
  );

  return { diff, numOfDiffPixels };
}

export default function pixelMatchPlugin() {
  return {
    name: 'pixel-match-command',

    async executeCommand({ command, payload, session }) {
      if (command === 'pixel-match') {
        if (session.browser.type === 'playwright') {
          return true;

          // eslint-disable-next-line no-unreachable
          const page = session.browser.getPage(session.id);

          const screenshot = await page.screenshot({ fullPage: true });
          const filePath = `./__screenshots__/master/${payload}.png`;

          if (!fs.existsSync(filePath)) {
            console.log(
              'Nothing to run pixel match against. Generate a screenshot and move it into __screenshots__/master first. The pipeline should do this on merge into master.',
            );
            return true;
          }

          const pngImage1 = PNG.sync.read(fs.readFileSync(filePath));
          const pngImage2 = PNG.sync.read(screenshot);

          try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { numOfDiffPixels, diff } = createDiff(pngImage1, pngImage2, {
              threshold: 0.1,
            });
            fs.writeFileSync(
              `./__screenshots__/diffs/${payload}.png`,
              PNG.sync.write(diff),
            );
            return numOfDiffPixels;
          } catch (e) {
            console.log(e);
            return false;
          }
        }

        // you might not be able to support all browser launchers
        throw new Error(
          `Taking screenshots is not supported for browser type ${session.browser.type}.`,
        );
      }
      return undefined;
    },
  };
}
